import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Optional, Output } from '@angular/core'

export interface CheckboxFilterOption {
    value: string
    label: string
}

@Component({
    selector: 'app-checkbox-filter-dropdown',
    templateUrl: './checkbox-filter-dropdown.component.html',
    styleUrls: ['./checkbox-filter-dropdown.component.scss'],
    standalone: false
})
export class CheckboxFilterDropdownComponent implements AfterViewInit, OnDestroy {
    @Input() title = 'Filter'
    @Input() options: CheckboxFilterOption[] = []
    @Input() excludedValues: ReadonlySet<string> = new Set<string>()
    @Input() searchPlaceholder = 'Search...'
    @Input() emptyText = 'No options'
    @Input() noMatchesText = 'No matches'
    @Input() searchEnabled = true

    @Output() optionToggled = new EventEmitter<string>()
    @Output() selectAllRequested = new EventEmitter<string[]>()
    @Output() deselectAllRequested = new EventEmitter<string[]>()
    @Output() clearRequested = new EventEmitter<void>()

    search = ''
    dropdownTop = 0
    dropdownLeft = 0
    dropdownMaxHeight = 320
    isPositioned = false
    private readonly repositionOnScroll = (): void => this.updatePosition()

    @HostBinding('style.top.px') get hostTop(): number {
        return this.dropdownTop
    }

    @HostBinding('style.left.px') get hostLeft(): number {
        return this.dropdownLeft
    }

    @HostBinding('style.max-height.px') get hostMaxHeight(): number {
        return this.dropdownMaxHeight
    }

    @HostBinding('class.is-positioned') get positionedClass(): boolean {
        return this.isPositioned
    }

    constructor(@Optional() private host?: ElementRef<HTMLElement>) {}

    ngAfterViewInit(): void {
        document.addEventListener('scroll', this.repositionOnScroll, true)
        setTimeout(() => this.updatePosition())
    }

    ngOnDestroy(): void {
        document.removeEventListener('scroll', this.repositionOnScroll, true)
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    updatePosition(): void {
        if (!this.host || typeof window === 'undefined') return

        const anchor = this.host.nativeElement.parentElement
        if (!anchor) return

        const anchorRect = anchor.getBoundingClientRect()
        const dropdownWidth = this.host.nativeElement.offsetWidth || 260
        const viewportPadding = 8
        const maxLeft = window.innerWidth - dropdownWidth - viewportPadding
        const belowTop = anchorRect.bottom + 4

        this.dropdownTop = belowTop
        this.dropdownLeft = Math.max(viewportPadding, Math.min(anchorRect.left, maxLeft))
        this.dropdownMaxHeight = Math.max(160, window.innerHeight - belowTop - viewportPadding)
        this.isPositioned = true
    }

    get hasSearch(): boolean {
        return this.search.trim().length > 0
    }

    get filteredOptions(): CheckboxFilterOption[] {
        const q = this.search.trim().toLowerCase()
        if (!q) return this.options
        return this.options.filter(option => option.label.toLowerCase().includes(q))
    }

    get selectedCount(): number {
        return this.options.filter(option => this.isSelected(option.value)).length
    }

    get filteredSelectedCount(): number {
        return this.filteredOptions.filter(option => this.isSelected(option.value)).length
    }

    get allFilteredSelected(): boolean {
        return this.filteredOptions.length > 0 && this.filteredSelectedCount === this.filteredOptions.length
    }

    get someFilteredSelected(): boolean {
        return this.filteredSelectedCount > 0 && !this.allFilteredSelected
    }

    get summaryText(): string {
        if (this.options.length === 0) return 'No options'

        let selectedText: string
        if (this.selectedCount === this.options.length) {
            selectedText = `All ${this.options.length} selected`
        } else if (this.selectedCount === 0) {
            selectedText = 'None selected'
        } else {
            selectedText = `${this.selectedCount} of ${this.options.length} selected`
        }

        if (!this.hasSearch) return selectedText

        const matchLabel = this.filteredOptions.length === 1 ? 'match' : 'matches'
        return `${this.filteredOptions.length} ${matchLabel}, ${selectedText}`
    }

    get selectAllCheckboxText(): string {
        return this.hasSearch ? 'Select all search results' : 'Select all'
    }

    get hasActiveFilter(): boolean {
        return this.selectedCount < this.options.length
    }

    get canClearFilter(): boolean {
        return this.hasActiveFilter || this.hasSearch
    }

    isSelected(value: string): boolean {
        return !this.excludedValues.has(value)
    }

    toggleOption(value: string): void {
        this.optionToggled.emit(value)
    }

    setFilteredSelection(selected: boolean): void {
        const values = this.filteredOptions.map(option => option.value)
        if (selected) {
            this.selectAllRequested.emit(values)
        } else {
            this.deselectAllRequested.emit(values)
        }
    }

    clearFilter(): void {
        this.search = ''
        this.clearRequested.emit()
    }
}
